import { Link } from "react-router-dom";
import { ShieldCheck, CreditCard, Lock, Users, MessageSquare, Scale, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead, { generateWebPageSchema } from "@/components/SEOHead";

const SECTIONS = [
  {
    icon: ShieldCheck,
    title: "Email Verification",
    body: "Every user on Paradise Borrowed verifies their email address before they can lend or borrow. This ensures all accounts are tied to real, reachable email addresses and helps protect against fraud and spam.",
  },
  {
    icon: CreditCard,
    title: "Secure Payments",
    body: "All transactions are processed through Stripe — an industry-leading payment platform with built-in fraud protection. Your financial information is never stored on our servers. Payments are held securely until the borrowing period begins, so lenders are always protected.",
  },
  {
    icon: Lock,
    title: "Damage Protection",
    body: "If an item is returned damaged, lenders can file a claim within 48 hours of the return date. Our Trust & Safety team reviews all claims promptly and works with both parties to resolve the cost fairly. We take evidence from both sides before making a decision.",
  },
  {
    icon: Users,
    title: "Community Standards",
    body: "We hold every user to a high standard of behavior. Users who violate our Terms of Service — including misrepresenting items, failing to return items, or engaging in fraudulent activity — face account suspension or permanent removal from the platform. We take community integrity seriously.",
  },
  {
    icon: MessageSquare,
    title: "Secure Messaging",
    body: "All communication between lenders and borrowers happens through our built-in messaging system. This keeps your personal contact information private and gives our team a record of conversations if a dispute arises.",
  },
];

export default function TrustAndSafety() {
  const seoData = {
    title: "Trust & Safety | Paradise Borrowed",
    description: "Learn how Paradise Borrowed protects lenders and borrowers with verified identities, secure payments, security deposits, and community standards.",
    url: "https://www.paradiseborrowed.com/trust-and-safety",
  };

  return (
    <>
      <SEOHead title={seoData.title} description={seoData.description} canonicalUrl={seoData.url} schema={generateWebPageSchema(seoData)} />
      <div className="bg-[#FAFAF8]" data-testid="trust-and-safety-page">
        {/* Hero */}
        <div className="max-w-4xl mx-auto px-4 pt-16 pb-12 text-center">
          <p className="text-xs tracking-widest uppercase text-stone-400 mb-4 font-ui">Trust & Safety</p>
          <h1 className="text-4xl sm:text-5xl text-stone-900 mb-4" style={{ fontFamily: "'Fraunces', serif", fontWeight: 400 }}>
            Your Safety Is Our Priority
          </h1>
          <p className="text-stone-500 max-w-xl mx-auto leading-relaxed font-ui">
            Paradise Borrowed is built on trust. Here's how we protect every lender and borrower on our platform.
          </p>
        </div>

        {/* Sections */}
        <div className="max-w-3xl mx-auto px-4 pb-8 space-y-6">
          {SECTIONS.map((section) => {
            const Icon = section.icon;
            return (
              <div key={section.title} className="bg-white border border-stone-200 rounded-2xl p-8" data-testid={`trust-section-${section.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-coral/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-coral" />
                  </div>
                  <div>
                    <h2 className="text-xl text-stone-900 mb-3" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>{section.title}</h2>
                    <p className="text-stone-600 text-sm leading-relaxed font-ui">{section.body}</p>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Dispute Resolution link section */}
          <div className="bg-white border border-stone-200 rounded-2xl p-8" data-testid="trust-section-dispute-resolution">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-coral/10 flex items-center justify-center shrink-0">
                <Scale className="w-5 h-5 text-coral" />
              </div>
              <div>
                <h2 className="text-xl text-stone-900 mb-3" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>Our Dispute Resolution Process</h2>
                <p className="text-stone-600 text-sm leading-relaxed font-ui mb-4">
                  If something goes wrong with a booking, we're here to help. Borrowers and lenders can submit a dispute within 48 hours, and our team reviews every case individually. We aim to resolve disputes fairly and quickly.
                </p>
                <Link to="/trust" className="text-coral text-sm font-medium font-ui inline-flex items-center gap-1 hover:gap-2 transition-all">
                  Learn more about our dispute process <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center">
            <h2 className="text-2xl text-stone-900 mb-3" style={{ fontFamily: "'Fraunces', serif", fontWeight: 400 }}>Have Questions About Safety?</h2>
            <p className="text-stone-500 text-sm font-ui mb-6">We're here to help. Reach out to our team anytime.</p>
            <Link to="/contact">
              <Button className="rounded-full px-8 bg-coral hover:bg-[#d44814] text-white font-ui" data-testid="trust-contact-cta">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
